import { makeAutoObservable } from 'mobx'
import AuthStore from 'auth/AuthStore'
import { trialCache } from 'cache/trialCache'
import { FormHandlerConfig, formHandler } from 'reusable/mobx-store-utils/form-handlers/new/form-handler'
import { signIn, signUp } from 'aws-amplify/auth'
import Loader from 'reusable/mobx-helpers/Loader'
import RegionRouterStore from 'features/main/RegionRouterStore'
import SignUpAdvisorStore from '../../SignUpAdvisorStore'
import CognitoSignUpException from '@gap/utils/auth/CognitoSignUpException'
import variables from 'variables/variables'
import { snackbarError } from 'reusable/mui/snackbar/snackbar'
import AuthAnalytics from 'analytics/AuthAnalytics'
import { identifyHubspotContact } from 'analytics/hubspot'
import { createStoreContext } from 'reusable/mobx-store-utils/storeUtils'
import { UserDto } from '@gap/backend/app-services/user/models/UserDto'
import { orgContextCache } from 'cache/orgContextCache'
import { UserTrialSetupStepEnum } from '@gap/backend/app-services/user/models/UserTrialStepDto'
import { apiClient } from 'api/trpc/trpc-client'
import Debug from 'reusable/utils/debug'
import { encryptWithPublicKey } from 'reusable/utils/challenge'

interface AdvisorCreateAccountStoreProps {
   authStore: AuthStore
   regionRouterStore: RegionRouterStore
   signUpAdvisorStore: SignUpAdvisorStore
}

export enum CreateAccountState {
   initial = 'initial',
   verifying = 'verifying',
   selectOrg = 'select-org'
}

export default class SignUpAdvisorCreateAccountStore {
   sp
   action
   loader = new Loader()
   isEmailSignupExpanded: boolean = false
   createAccountState: CreateAccountState = CreateAccountState.initial

   userAlreadyExists = false

   fields = {
      email: '',
      password: '',
      firstName: '',
      lastName: '',
   }

   constructor(sp: AdvisorCreateAccountStoreProps) {
      this.sp = sp
      makeAutoObservable(this)
      this.action = sp.signUpAdvisorStore.action
      if (!this.canModifyEmailAddress) {
         this.fields.email = this.inviteeEmail || ''
      }
      const cognito = sp.authStore.cognitoAttributes
      if (cognito) {
         if (!this.fields.firstName && cognito.firstName) {
            this.fields.firstName = cognito.firstName
         }
         if (!this.fields.lastName && cognito.lastName) {
            this.fields.lastName = cognito.lastName
         }
         if (!this.fields.email && cognito.email) {
            this.fields.email = cognito.email
         }
      }
   }

   get formHandler() {
      return formHandler(this.fields, {
         firstName: { type: 'string' },
         lastName: { type: 'string' },
         email: { type: 'string' },
         password: { type: 'string' },
      } satisfies FormHandlerConfig)
   }

   get title() {
      if (this.action === 'register') {
         return `Join your team at ${this.sp.signUpAdvisorStore.invitationDetails.fromOrg}`
      } else {
         return `Welcome to ${variables.gapName}`
      }
   }

   get subTitle() {
      if (this.action === 'register') {
         return `${this.sp.signUpAdvisorStore.invitationDetails.fromName} has invited you to join their team on ${variables.gapName}. Create your account below to start collaborating.`
      } else {
         return `Get instant access to meeting tools, templates, education, and AI-powered support for accountants and advisors.`
      }
   }

   get email() {
      return trialCache.verificationData.get()?.email || this.fields.email
   }

   get inviteeEmail() {
      return this.sp.signUpAdvisorStore.invitationDetails.email
   }

   get canModifyEmailAddress() {
      return this.action !== 'register'
   }

   navigateToLogin = () => {
      this.sp.regionRouterStore.navigate('/')
   }

   expandEmailSignup = () => {
      this.isEmailSignupExpanded = true
   }

   createCognitoAccount = async () => {
      this.userAlreadyExists = false
      if (this.formHandler.validate(UserDto.createTrialUserValidationSchema)) {
         try {
            await this.loader.executeLoading(async () => {
               // Signup no longer requires verification. Directly login the user in, triggering email MFA verification
               await signUp(this.authDetails({ withOptions: true }))
               await signIn(this.authDetails({ withOptions: false }))

               this.sp.authStore.email = this.fields.email

               if (this.sp.signUpAdvisorStore.invitationDetails.orgId) {
                  // Set orgId(if found) in header, ensuring correct api calls from this point forwards
                  orgContextCache.authOrgId.set(this.sp.signUpAdvisorStore.invitationDetails.orgId)
               }

               apiClient.user.ensureIAmAccepted.mutate()

               this.createAccountState = CreateAccountState.verifying
               AuthAnalytics.advisorAccountCreated()
            })
         } catch (error: any) {
            this.handleSignUpError(error)
         }
      }
   }

   onAccountVerification = async () => {
      identifyHubspotContact(this.email)
      if (this.userAlreadyExists) {
         // If user exists but is a guest, update the password
         const publicKey = await apiClient.challenge.createIfIAmAGuest.mutate()
         if (publicKey) {
            const encryptedPassword = await encryptWithPublicKey({
               text: this.fields.password,
               publicKey
            })
            await apiClient.user.updateMyPassword.mutate({ encryptedPassword })
         }

         if (this.sp.signUpAdvisorStore.invitationDetails.orgId) {
            // Set orgId(if found) in header, ensuring correct api calls from this point forwards
            orgContextCache.authOrgId.set(this.sp.signUpAdvisorStore.invitationDetails.orgId)
         }

         this.sp.authStore.email = this.fields.email
         await apiClient.user.ensureIAmAccepted.mutate().catch(() => undefined)
         AuthAnalytics.advisorAccountCreated()

         await this.sp.authStore.updateUserAttributes({
            'custom:trialStep': this.action === 'register' ? UserTrialSetupStepEnum.completed : UserTrialSetupStepEnum.orgSetup
         })

         if (this.action === 'register') {
            return this.sp.regionRouterStore.navigate(`/`, true)
         }
         return this.sp.regionRouterStore.navigate(`/trial`, true)
      }
      if (this.action === 'register') {
         await apiClient.user.ensureIAmAccepted.mutate().catch(() => undefined)
         return this.sp.regionRouterStore.navigate(`/`, true)
      }
      return this.sp.regionRouterStore.navigate(`/trial/org-setup`)
   }

   private authDetails(params: { withOptions: boolean }) {
      return {
         username: this.fields.email,
         password: this.fields.password,
         ...params.withOptions ? {
            options: {
               userAttributes: {
                  email: this.fields.email,
                  given_name: this.fields.firstName,
                  family_name: this.fields.lastName,
                  'custom:trialStep': UserTrialSetupStepEnum[this.action === 'register' ? 'completed' : 'orgSetup']
               },
               autoSignIn: true
            }
         } : {}
      }
   }

   private async handleSignUpError(error: { name: CognitoSignUpException, message: string }) {
      switch (error.name) {
         case CognitoSignUpException['UsernameExistsException']: {
            this.userAlreadyExists = true
            await this.handleUsernameExistsError()
            break
         }
         case CognitoSignUpException['InvalidParameterException']:
            if (error.message === 'Username should be an email.') {
               snackbarError('Must be a valid email address.')
            } else {
               const [, policy] = /Password did not conform with policy: (.+)/.exec(error.message) || []
               snackbarError(policy)
            }
            break
         case CognitoSignUpException['UserLambdaValidationException']:
            snackbarError('Unable to complete sign up. Please try again or contact support.')
            break
         default:
            snackbarError(error.message || 'An error occurred during sign up')
      }
   }

   private async handleUsernameExistsError() {
      try {
         await this.loader.executeLoading(async () => {
            const response = await signIn({
               username: this.fields.email,
               options: {
                  authFlowType: 'CUSTOM_WITHOUT_SRP'
               }
            })

            if (response.nextStep.signInStep !== "CONFIRM_SIGN_IN_WITH_CUSTOM_CHALLENGE") {
               Debug.log.auth('Email based login failed', response)
               return snackbarError('Something went wrong. Please try again.')
            }

            this.createAccountState = CreateAccountState.verifying
         })
      } catch {
         snackbarError('Unable to complete sign up. Please try again or contact support.')
      }
   }
}

export const { Provider: SignUpAdvisorCreateAccountStoreProvider, useProvidedStore: useSignUpAdvisorCreateAccountStore } = createStoreContext(SignUpAdvisorCreateAccountStore)
